import { createContext, useContext, useState, useRef, useCallback } from 'react';
import type { ReactNode } from 'react';

export interface PodcastMiniData {
  podcastId: string;
  title: string;
  thumbnailUrl: string;
  isPlaying: boolean;
  subtitleText: string;
}

interface PlayerControls {
  playPause: () => void;
}

interface PodcastKeepaliveContextValue {
  /** ID of the podcast currently kept alive (null = nothing playing) */
  persistedPodcastId: string | null;
  setPersisted: (id: string) => void;
  clearPersisted: () => void;
  /** Mini-player display data — updated by PodcastStudy */
  miniData: PodcastMiniData | null;
  updateMiniData: (data: PodcastMiniData) => void;
  /** Player controls exposed by PodcastStudy */
  getPlayerControls: () => PlayerControls | null;
  setPlayerControls: (controls: PlayerControls) => void;
}

const Ctx = createContext<PodcastKeepaliveContextValue | null>(null);

export function PodcastKeepaliveProvider({ children }: { children: ReactNode }) {
  const [persistedPodcastId, setPersistedPodcastId] = useState<string | null>(null);
  // Use state for miniData so MiniPlayerBar re-renders on subtitle/play-state changes.
  // Updates happen at natural speech pace (~every 5-30s) so overhead is negligible.
  const [miniData, setMiniData] = useState<PodcastMiniData | null>(null);
  const playerControlsRef = useRef<PlayerControls | null>(null);

  const setPersisted = useCallback((id: string) => {
    setPersistedPodcastId(id);
  }, []);

  const clearPersisted = useCallback(() => {
    setPersistedPodcastId(null);
    setMiniData(null);
    playerControlsRef.current = null;
  }, []);

  const updateMiniData = useCallback((data: PodcastMiniData) => {
    setMiniData(data);
  }, []);

  const setPlayerControls = useCallback((controls: PlayerControls) => {
    playerControlsRef.current = controls;
  }, []);

  const getPlayerControls = useCallback(() => playerControlsRef.current, []);

  return (
    <Ctx.Provider
      value={{
        persistedPodcastId,
        setPersisted,
        clearPersisted,
        miniData,
        updateMiniData,
        getPlayerControls,
        setPlayerControls,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function usePodcastKeepalive() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('usePodcastKeepalive must be inside PodcastKeepaliveProvider');
  return ctx;
}
